import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Mail, MapPin, Phone } from 'lucide-react'
import { CONTACT_INFO, ROUTES, productPath } from '@/lib/constants'
import { PRODUCTS } from '@/lib/products'

export default function Footer() {
  const { t, i18n } = useTranslation(['common', 'products'])
  const lang = (i18n.resolvedLanguage ?? 'he') as keyof typeof CONTACT_INFO.address

  return (
    <footer className="border-t bg-muted/40">
      <div className="container py-14">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <img src="/images/tdrem-logo.png" alt="Tdrem" className="h-12 w-auto dark:brightness-[1.8] dark:saturate-50" />
            <p className="max-w-xs text-sm leading-relaxed text-muted-foreground">{t('footer.tagline')}</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-foreground">
                  {CONTACT_INFO.email}
                </a>
              </li>
              {CONTACT_INFO.phone && (
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <a href={`tel:${CONTACT_INFO.phone.replace(/[^+\d]/g, '')}`} dir="ltr" className="hover:text-foreground">
                    {CONTACT_INFO.phone}
                  </a>
                </li>
              )}
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {CONTACT_INFO.address[lang] ?? CONTACT_INFO.address.en}
              </li>
            </ul>
          </div>

          {/* Products */}
          <FooterCol title={t('footer.products')}>
            {PRODUCTS.map((p) => (
              <li key={p.id}>
                <Link to={productPath(p.slug)} className="hover:text-foreground">
                  {t(`products:${p.id}.name`)}
                </Link>
              </li>
            ))}
          </FooterCol>

          {/* Sign in */}
          <FooterCol title={t('footer.apps')}>
            {PRODUCTS.filter((p) => p.status !== 'comingSoon').map((p) => (
              <li key={p.id}>
                <a
                  href={p.appUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  dir="ltr"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                >
                  {p.appHost}
                  <ArrowUpRight className="h-3 w-3" />
                </a>
              </li>
            ))}
          </FooterCol>

          {/* Company */}
          <FooterCol title={t('footer.company')}>
            <li>
              <Link to={ROUTES.ABOUT} className="hover:text-foreground">
                {t('nav.about')}
              </Link>
            </li>
            <li>
              <Link to={ROUTES.PRODUCTS} className="hover:text-foreground">
                {t('nav.allProducts')}
              </Link>
            </li>
            <li>
              <Link to={ROUTES.CONTACT} className="hover:text-foreground">
                {t('nav.contact')}
              </Link>
            </li>
          </FooterCol>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t pt-6 text-sm text-muted-foreground md:flex-row">
          <p>
            © {new Date().getFullYear()} Tdrem. {t('footer.rights')}
          </p>
          <p className="font-medium tracking-wide">Think · Develop · REMaster</p>
        </div>
      </div>
    </footer>
  )
}

function FooterCol({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-4 text-sm font-semibold">{title}</h3>
      <ul className="space-y-2.5 text-sm text-muted-foreground">{children}</ul>
    </div>
  )
}
